import { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconButton, Popover } from '@mui/material';
import * as MuiDatePickers from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers/icons';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import classNames from 'classnames';
import moment from 'moment';

import 'moment/locale/en-gb';

import { DAY_FIRST_CALENDAR_FORMAT } from '../../lib/constants/date-time.constants';
import lang from '../../lib/language';
import { isBeforeDate } from '../../lib/util/DateTimeHelpers';

import ClearIcon from '../../assets/img/close-circle.svg';
import DateRangeIcon from '../../assets/img/date-range-icon.svg';
import DownArrow from '../../assets/img/icon_expan_down_ratios.svg';
import { useBrowserWidth } from '../../hooks';
import { ZOOM_BEHAVIOR_WIDTH } from '../../util/constants';
import { UI_ROUTES } from '../../util/routes';
import { DateRangeState } from '../../util/types';
import Button from '../Button/Button';
import { ButtonTypesEnum } from '../Button/Button.types';
import ClickableImage from '../ClickableImage/ClickableImage';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';

import {
  clampEndDateToRangeLimit,
  createDateRangeSyncSnapshot,
  DATE_RANGE_KEY,
  formatDateToMarketData,
  formatInputDateValue,
  getDateRangeByDropdownOption,
  getRangeLimitedMaxDate,
  isDateRangeSyncSnapshotChanged,
  resolveDateRangeDropdownOption,
  resolveDateRangeFromProps,
} from './dateRangeDropdownHelpers';
import { CalendarViewEnum, DateRangeDropdownOptionEnum, HoverExtendedRangePositionEnum, RangeSelectionFieldEnum } from './DateRangePickerWrapper.types';

import styles from './DateRangePickerWrapper.module.scss';

const muiDatePickersExports = MuiDatePickers as Record<string, any>;
const PickersDay = (
  muiDatePickersExports['PickersDay']
  ?? muiDatePickersExports['PickerDay']
) as ComponentType<any>;

type CalendarHeaderProps = {
  currentMonth: moment.Moment;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  onMonthChange: (date: moment.Moment, direction: 'left' | 'right') => void;
  view: 'day' | 'month' | 'year';
  disablePast?: boolean;
  disableFuture?: boolean;
};

type Props = {
  minDateState?: string;
  maxDateState?: string;
  minDateCalendar?: Date | string;
  maxDateCalendar?: Date | string;
  initialState: DateRangeState;
  onDateSelect?: (date: DateRangeState) => void;
  withDateRangeDropdown?: boolean;
  resetRangeState?: () => void;
  maxRangeInDays?: number;
  singleDateSelection?: boolean;
};

const DateRangePickerWrapper = ({
  minDateState,
  maxDateState,
  minDateCalendar,
  maxDateCalendar,
  initialState,
  onDateSelect,
  withDateRangeDropdown = false,
  resetRangeState,
  maxRangeInDays,
  singleDateSelection = false,
}: Props) => {
  const HALF_VIEWPORT_DIVISOR = 2;
  const location = useLocation();
  const { browserWidthSize } = useBrowserWidth();

  const dropdownBaseDate = minDateCalendar ?? initialState?.startDate ?? new Date();
  const initialStateStartKey = formatInputDateValue(initialState?.startDate);
  const initialStateEndKey = formatInputDateValue(initialState?.endDate);
  const dropdownBaseDateKey = formatInputDateValue(dropdownBaseDate);
  const { startDate: initialStartDate, endDate: initialEndDate } = resolveDateRangeFromProps({
    minDateState,
    maxDateState,
    initialState,
    withDateRangeDropdown,
    dropdownBaseDate,
  });
  const [dateRangeState, setDateRangeState] = useState<DateRangeState>({
    startDate: initialStartDate,
    endDate: initialEndDate,
    key: DATE_RANGE_KEY,
  });
  const [tempRange, setTempRange] = useState<DateRangeState>(dateRangeState);
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false);
  const [displayDropdown, setDisplayDropdown] = useState<boolean>(false);
  // Written only in the handlers that open the popover, never on close, so the closing
  // Popover keeps rendering the content it was opened with through its exit transition.
  const lastOpenContentRef = useRef<'dropdown' | 'calendar'>('dropdown');
  const [selectedDropdownOption, setSelectedDropdownOption] = useState<DateRangeDropdownOptionEnum>(
    () => (
      withDateRangeDropdown
        ? resolveDateRangeDropdownOption(initialStartDate, initialEndDate, dropdownBaseDate)
        : DateRangeDropdownOptionEnum.CustomDate
    ),
  );
  const [view, setView] = useState<CalendarViewEnum>(CalendarViewEnum.Day);
  // eslint-disable-next-line
  const [selectedRangeField, setSelectedRangeField] = useState<RangeSelectionFieldEnum>(RangeSelectionFieldEnum.StartDate);
  const [inputValueStartDate, setInputValueStartDate] = useState<string | undefined>(
    minDateState ?? formatInputDateValue(initialState?.startDate),
  );
  const [inputValueEndDate, setInputValueEndDate] = useState<string | undefined>(
    maxDateState ?? formatInputDateValue(initialState?.endDate),
  );
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showClearButton, setShowClearButton] = useState<boolean>(false);
  const [paperPopover, setPaperPopover] = useState<number>(90);
  const [hoveredDate, setHoveredDate] = useState<moment.Moment | null>(null);
  const getInitialNavigationDate = (): moment.Moment | null => {
    if (initialStartDate) return moment(initialStartDate);
    if (initialEndDate) return moment(initialEndDate);
    return null;
  };

  // eslint-disable-next-line
  const [calendarNavigationDate, setCalendarNavigationDate] = useState<moment.Moment | null>(getInitialNavigationDate());
  const syncedExternalRangeRef = useRef(createDateRangeSyncSnapshot(initialStartDate, initialEndDate));

  const minDateMoment = useMemo(() => (minDateCalendar ? moment(minDateCalendar) : undefined), [minDateCalendar]);
  const maxDateMoment = useMemo(() => (maxDateCalendar ? moment(maxDateCalendar) : undefined), [maxDateCalendar]);
  const effectiveMaxDate = useMemo(() => getRangeLimitedMaxDate({
    startDate: tempRange.startDate,
    maxRangeInDays,
    maxDateCalendar: maxDateMoment,
    isEndDateSelection: !singleDateSelection && selectedRangeField === RangeSelectionFieldEnum.EndDate,
  }), [maxDateMoment, maxRangeInDays, selectedRangeField, singleDateSelection, tempRange.startDate]);

  const calendarValue = useMemo(() => {
    if (calendarNavigationDate) {
      return calendarNavigationDate;
    }

    if (tempRange.startDate) {
      return moment(tempRange.startDate);
    }

    if (tempRange.endDate) {
      return moment(tempRange.endDate);
    }

    return null;
  }, [calendarNavigationDate, tempRange.endDate, tempRange.startDate]);

  const syncInputValues = (startDate?: Date | string, endDate?: Date | string) => {
    const formattedStartDate = formatInputDateValue(startDate);
    const formattedEndDate = formatInputDateValue(endDate);

    setInputValueStartDate(formattedStartDate);
    setInputValueEndDate(formattedEndDate);
  };

  const updateRange = (startDate?: Date | string, endDate?: Date | string) => {
    const resolvedEndDate = singleDateSelection ? (startDate ?? endDate) : endDate;
    const nextRange = { startDate, endDate: resolvedEndDate, key: DATE_RANGE_KEY };

    setDateRangeState(nextRange);
    setTempRange(nextRange);
    syncInputValues(startDate, resolvedEndDate);
  };

  const emitDateSelection = (startDate?: Date | string, endDate?: Date | string) => {
    onDateSelect?.({
      startDate: formatDateToMarketData(startDate),
      endDate: formatDateToMarketData(endDate),
    });
  };

  const getNormalizedTempRange = (): DateRangeState => (
    singleDateSelection && dateRangeState.startDate
      ? { ...dateRangeState, endDate: dateRangeState.startDate }
      : dateRangeState
  );

  const getDropdownOptionLabel = (option: DateRangeDropdownOptionEnum): string => {
    if (option === DateRangeDropdownOptionEnum.Tomorrow) {
      return lang.commonDateRangeTomorrow();
    }

    if (option === DateRangeDropdownOptionEnum.OneWeek) {
      return lang.commonDateRangeOneWeek();
    }

    if (option === DateRangeDropdownOptionEnum.OneMonth) {
      return lang.commonDateRangeOneMonth();
    }

    return lang.commonDateRangeCustomDate();
  };

  const applyDropdownOption = (option: DateRangeDropdownOptionEnum) => {
    const { startDate, endDate } = getDateRangeByDropdownOption(option, dropdownBaseDate);

    updateRange(startDate, endDate);
    setSelectedDropdownOption(option);
    setDisplayDropdown(false);
    setDisplayCalendar(false);
    setAnchorEl(null);
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setHoveredDate(null);
    setView(CalendarViewEnum.Day);
    emitDateSelection(startDate, endDate);
  };

  const handleDaySelect = (value: moment.Moment | null) => {
    if (!value) {
      return;
    }

    const selectedDate = value.toDate();
    const isDayView = view === CalendarViewEnum.Day;

    if (!isDayView) {
      setCalendarNavigationDate(moment(selectedDate));

      if (view === CalendarViewEnum.Year) {
        setView(CalendarViewEnum.Month);
      }

      if (view === CalendarViewEnum.Month) {
        setView(CalendarViewEnum.Day);
      }

      setHoveredDate(null);
      return;
    }

    if (singleDateSelection) {
      setTempRange({
        startDate: selectedDate,
        endDate: selectedDate,
        key: DATE_RANGE_KEY,
      });

      setCalendarNavigationDate(moment(selectedDate));
      setHoveredDate(null);
      return;
    }

    if (selectedRangeField === RangeSelectionFieldEnum.StartDate) {
      const fallbackEndDate = tempRange.endDate
        && isBeforeDate(tempRange.endDate, selectedDate) ? selectedDate : (tempRange.endDate ?? selectedDate);
      const nextEndDate = clampEndDateToRangeLimit(selectedDate, fallbackEndDate, maxRangeInDays);

      setTempRange({
        startDate: selectedDate,
        endDate: nextEndDate,
        key: DATE_RANGE_KEY,
      });

      setSelectedRangeField(RangeSelectionFieldEnum.EndDate);
      setCalendarNavigationDate(moment(nextEndDate ?? selectedDate));
      return;
    }

    const nextStartDate = tempRange.startDate
      && isBeforeDate(selectedDate, tempRange.startDate) ? selectedDate : (tempRange.startDate ?? selectedDate);

    setTempRange({
      startDate: nextStartDate,
      endDate: selectedDate,
      key: DATE_RANGE_KEY,
    });

    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setCalendarNavigationDate(moment(selectedDate));
    setHoveredDate(null);
  };

  const isRangeStartDate = (day: moment.Moment): boolean => {
    if (!tempRange.startDate) {
      return false;
    }

    return day.isSame(moment(tempRange.startDate), 'day');
  };

  const isRangeEndDate = (day: moment.Moment): boolean => {
    if (!tempRange.endDate) {
      return false;
    }

    return day.isSame(moment(tempRange.endDate), 'day');
  };

  const isDateInRange = (day: moment.Moment): boolean => {
    if (!tempRange.startDate || !tempRange.endDate) return false;

    const start = moment(tempRange.startDate).startOf('day');
    const end = moment(tempRange.endDate).startOf('day');
    const current = moment(day).startOf('day');

    if (start.isSame(end, 'day')) return false;

    return current.isAfter(start) && current.isBefore(end);
  };

  const isHoverExtendedRangeDate = (day: moment.Moment): boolean => {
    if (singleDateSelection || !hoveredDate || !tempRange.startDate || !tempRange.endDate) {
      return false;
    }

    const hovered = hoveredDate.clone().startOf('day');
    const start = moment(tempRange.startDate).startOf('day');
    const end = moment(tempRange.endDate).startOf('day');
    const current = day.clone().startOf('day');

    if (selectedRangeField === RangeSelectionFieldEnum.StartDate && hovered.isBefore(start, 'day')) {
      return current.isSameOrAfter(hovered, 'day') && current.isBefore(start, 'day');
    }

    if (selectedRangeField === RangeSelectionFieldEnum.EndDate && hovered.isAfter(end, 'day')) {
      return current.isAfter(end, 'day') && current.isSameOrBefore(hovered, 'day');
    }

    return false;
  };

  const getHoverExtendedRangePosition = (day: moment.Moment): HoverExtendedRangePositionEnum => {
    if (!hoveredDate || !tempRange.startDate || !tempRange.endDate) {
      return HoverExtendedRangePositionEnum.None;
    }

    const hovered = hoveredDate.clone().startOf('day');
    const start = moment(tempRange.startDate).startOf('day');
    const end = moment(tempRange.endDate).startOf('day');
    const current = day.clone().startOf('day');

    let rangeStart: moment.Moment | null = null;
    let rangeEnd: moment.Moment | null = null;

    if (selectedRangeField === RangeSelectionFieldEnum.StartDate && hovered.isBefore(start, 'day')) {
      rangeStart = hovered;
      rangeEnd = start.clone().subtract(1, 'day');
    }

    if (selectedRangeField === RangeSelectionFieldEnum.EndDate && hovered.isAfter(end, 'day')) {
      rangeStart = end.clone().add(1, 'day');
      rangeEnd = hovered;
    }

    if (!rangeStart || !rangeEnd || !current.isBetween(rangeStart, rangeEnd, 'day', '[]')) {
      return HoverExtendedRangePositionEnum.None;
    }

    const isSingleDayRange = rangeStart.isSame(rangeEnd, 'day');
    if (isSingleDayRange) {
      return HoverExtendedRangePositionEnum.Single;
    }

    const previousDay = current.clone().subtract(1, 'day');
    const nextDay = current.clone().add(1, 'day');
    const isPreviousInRange = previousDay.isBetween(rangeStart, rangeEnd, 'day', '[]');
    const isNextInRange = nextDay.isBetween(rangeStart, rangeEnd, 'day', '[]');
    const isWeekRowStart = current.isoWeekday() === 1;
    const isWeekRowEnd = current.isoWeekday() === 7;
    const isMonthRowStart = current.date() === 1;
    const isMonthRowEnd = nextDay.date() === 1;

    const hasLeftBorder = !isPreviousInRange || isWeekRowStart || isMonthRowStart;
    const hasRightBorder = !isNextInRange || isWeekRowEnd || isMonthRowEnd;

    if (hasLeftBorder && hasRightBorder) {
      return HoverExtendedRangePositionEnum.Single;
    }

    if (hasLeftBorder) {
      return HoverExtendedRangePositionEnum.Left;
    }

    if (hasRightBorder) {
      return HoverExtendedRangePositionEnum.Right;
    }

    return HoverExtendedRangePositionEnum.Middle;
  };

  const RenderDay = (propsDay: any) => {
    const { day } = propsDay;
    const isStartDate = isRangeStartDate(day);
    const isEndDate = isRangeEndDate(day);
    const isSingleDateSelection = isStartDate && isEndDate;
    const isInRange = isDateInRange(day);
    const isInHoverExtendedRange = isHoverExtendedRangeDate(day);
    const hoverExtendedRangePosition = getHoverExtendedRangePosition(day);

    const isHoveredDay = Boolean(hoveredDate && day.isSame(hoveredDate, 'day'));
    const hasOutlinedLeft = Boolean(hoveredDate && isHoverExtendedRangeDate(hoveredDate.clone().subtract(1, 'day')));
    const hasOutlinedRight = Boolean(hoveredDate && isHoverExtendedRangeDate(hoveredDate.clone().add(1, 'day')));

    const BORDER_COLOR = '#3BB0FF26';
    const borderRadiusMap = {
      left: '8px 0 0 8px',
      right: '0 8px 8px 0',
      single: '8px',
    };

    const isStartDateSelectedToChange = selectedRangeField === RangeSelectionFieldEnum.StartDate;
    const isHoveredAtCalendarRightEdge = Boolean(isHoveredDay && (day.isoWeekday() === 7 || day.clone().add(1, 'day').date() === 1));
    const isStartAndHoveringRightEdge = isStartDateSelectedToChange && isHoveredAtCalendarRightEdge;

    const isEndDateSelectedToChange = selectedRangeField === RangeSelectionFieldEnum.EndDate;
    const isHoveredAtCalendarLeftEdge = Boolean(isHoveredDay && (day.isoWeekday() === 1 || day.date() === 1));
    const isEndAndHoveringRightEdge = isEndDateSelectedToChange && isHoveredAtCalendarLeftEdge;

    return (
      <PickersDay
        {...propsDay}
        disableMargin
        selected={isStartDate || isEndDate || isInRange}
        onMouseEnter={() => setHoveredDate(day)}
        onMouseLeave={() => setHoveredDate(null)}
        sx={{
          ...(isInHoverExtendedRange && {
            borderTop: `1px solid ${BORDER_COLOR}`,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            borderLeft:
              hoverExtendedRangePosition === HoverExtendedRangePositionEnum.Left
                || hoverExtendedRangePosition === HoverExtendedRangePositionEnum.Single
                ? `1px solid ${BORDER_COLOR}`
                : HoverExtendedRangePositionEnum.None,
            borderRight:
              hoverExtendedRangePosition === HoverExtendedRangePositionEnum.Right
                || hoverExtendedRangePosition === HoverExtendedRangePositionEnum.Single
                ? `1px solid ${BORDER_COLOR}`
                : HoverExtendedRangePositionEnum.None,
            borderRadius: borderRadiusMap[hoverExtendedRangePosition] ?? '0',
          }),
          ...(isHoveredDay && { position: 'relative' }),
          ...(isHoveredDay
            && !isEndAndHoveringRightEdge
            && hasOutlinedLeft && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-1px',
              top: '-0.8px',
              width: '85%',
              height: 'calc(100% + 2px)',
              borderTop: `1px solid ${BORDER_COLOR}`,
              borderBottom: `1px solid ${BORDER_COLOR}`,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            },
          }),
          ...(isHoveredDay
            && !isStartAndHoveringRightEdge
            && hasOutlinedRight && {
            '&::after': {
              content: '""',
              position: 'absolute',
              right: '-1px',
              top: '-1px',
              width: '85%',
              height: 'calc(100% + 2px)',
              borderTop: `1px solid ${BORDER_COLOR}`,
              borderBottom: `1px solid ${BORDER_COLOR}`,
              boxSizing: 'border-box',
              pointerEvents: 'none',
            },
          }),
          ...(isInRange && {
            backgroundColor: '#3BB0FF26 !important',
            borderRadius: '0 !important',
            color: '#F2F4F7 !important',
          }),
          ...(isStartDate
            && !isSingleDateSelection && {
            borderRadius: '8px 8px 8px 8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
            position: 'relative',

            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#3BB0FF26',
              borderRadius: '8px 0 0 8px',
              pointerEvents: 'none',
            },
          }),
          ...(isEndDate
            && !isSingleDateSelection && {
            borderRadius: '8px 8px 8px 8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
            position: 'relative',

            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#3BB0FF26',
              borderRadius: '0 8px 8px 0',
              pointerEvents: 'none',
            },
          }),
          ...(isSingleDateSelection && {
            borderRadius: '8px !important',
            backgroundColor: '#3BB0FF !important',
            color: '#0A0B13 !important',
          }),
        }}
      />
    );
  };

  const handleApply = () => {
    const { startDate } = tempRange;
    const endDate = singleDateSelection
      ? (startDate ?? tempRange.endDate)
      : (tempRange.endDate ?? tempRange.startDate);
    const inputValueFrom = formatDateToMarketData(startDate);
    const inputValueTo = formatDateToMarketData(endDate);

    updateRange(startDate, endDate);

    if (withDateRangeDropdown) {
      setSelectedDropdownOption(DateRangeDropdownOptionEnum.CustomDate);
      setDisplayDropdown(false);
    } else {
      setShowClearButton(true);
    }
    setErrorMessage('');
    setDisplayCalendar(false);
    setAnchorEl(null);
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setHoveredDate(null);
    setView(CalendarViewEnum.Day);
    onDateSelect?.({ startDate: inputValueFrom, endDate: inputValueTo });
  };

  const handleCancel = () => {
    setTempRange(getNormalizedTempRange());
    setDisplayCalendar(false);
    setDisplayDropdown(false);
    setAnchorEl(null);
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setHoveredDate(null);
    setView(CalendarViewEnum.Day);
  };

  const popoverClose = () => {
    setDisplayCalendar(false);
    setDisplayDropdown(false);
    setAnchorEl(null);
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setHoveredDate(null);
  };

  const openDateRangeCalendar = (e, rangeField = RangeSelectionFieldEnum.StartDate) => {
    if (withDateRangeDropdown) {
      if (selectedDropdownOption === DateRangeDropdownOptionEnum.CustomDate) {
        setTempRange(getNormalizedTempRange());
        setDisplayDropdown(false);
        setDisplayCalendar(true);
        lastOpenContentRef.current = 'calendar';
        setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
        setView(CalendarViewEnum.Day);
        if (dateRangeState.startDate) {
          setCalendarNavigationDate(moment(dateRangeState.startDate));
        }
        setAnchorEl(e.currentTarget);
        setHoveredDate(null);
        return;
      }

      setDisplayDropdown(true);
      setDisplayCalendar(false);
      lastOpenContentRef.current = 'dropdown';
      setAnchorEl(e.currentTarget);
      setHoveredDate(null);
      return;
    }

    setTempRange(getNormalizedTempRange());
    setDisplayCalendar(true);
    setAnchorEl(e.currentTarget);
    setSelectedRangeField(rangeField);
    if (rangeField === RangeSelectionFieldEnum.StartDate && dateRangeState.startDate) {
      setCalendarNavigationDate(moment(dateRangeState.startDate));
    }

    if (rangeField === RangeSelectionFieldEnum.EndDate && dateRangeState.endDate) {
      setCalendarNavigationDate(moment(dateRangeState.endDate));
    }
    setHoveredDate(null);
  };

  const clearDates = () => {
    setErrorMessage('');

    if (withDateRangeDropdown) {
      applyDropdownOption(DateRangeDropdownOptionEnum.OneMonth);
      return;
    }

    const resetEndDateSource = singleDateSelection
      ? (initialState?.startDate ?? initialState?.endDate)
      : initialState?.endDate;
    const resetStartDate = formatDateToMarketData(initialState?.startDate);
    const resetEndDate = formatDateToMarketData(resetEndDateSource);

    updateRange(initialState?.startDate, resetEndDateSource);
    setShowClearButton(false);
    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
    setHoveredDate(null);
    onDateSelect?.({ startDate: resetStartDate, endDate: resetEndDate });
    resetRangeState?.();
  };

  useEffect(() => {
    if (displayCalendar || displayDropdown) {
      return;
    }

    const nextExternalRange = resolveDateRangeFromProps({
      minDateState,
      maxDateState,
      initialState,
      withDateRangeDropdown,
      dropdownBaseDate,
    });
    const nextExternalSnapshot = createDateRangeSyncSnapshot(nextExternalRange.startDate, nextExternalRange.endDate);
    const currentInternalSnapshot = createDateRangeSyncSnapshot(dateRangeState.startDate, dateRangeState.endDate);
    const didExternalRangeChange = isDateRangeSyncSnapshotChanged(syncedExternalRangeRef.current, nextExternalSnapshot);
    const shouldSyncRange = isDateRangeSyncSnapshotChanged(currentInternalSnapshot, nextExternalSnapshot);

    if (didExternalRangeChange && shouldSyncRange) {
      updateRange(nextExternalRange.startDate, nextExternalRange.endDate);
      setCalendarNavigationDate(nextExternalRange.startDate ? moment(nextExternalRange.startDate) : null);
      setHoveredDate(null);
      setErrorMessage('');
    }

    if (withDateRangeDropdown && didExternalRangeChange) {
      const resolvedOption = resolveDateRangeDropdownOption(
        nextExternalRange.startDate,
        nextExternalRange.endDate,
        dropdownBaseDate,
      );

      if (selectedDropdownOption !== resolvedOption) {
        setSelectedDropdownOption(resolvedOption);
      }
    }

    syncedExternalRangeRef.current = nextExternalSnapshot;

    if (withDateRangeDropdown) {
      return;
    }

    if (didExternalRangeChange && !minDateState && !maxDateState && showClearButton) {
      setShowClearButton(false);
    }
  }, [
    displayCalendar,
    displayDropdown,
    dropdownBaseDateKey,
    initialStateEndKey,
    initialStateStartKey,
    maxDateState,
    minDateState,
    selectedDropdownOption,
    showClearButton,
    withDateRangeDropdown,
  ]);

  useEffect(() => {
    moment.updateLocale('en', {
      week: { dow: 1, doy: 4 },
    });

    setPaperPopover(90 - Math.floor(window.innerWidth / 10));
  }, [browserWidthSize]);

  const handleDropdownOptionSelect = (option: DateRangeDropdownOptionEnum) => {
    if (option === DateRangeDropdownOptionEnum.CustomDate) {
      setTempRange(getNormalizedTempRange());
      setDisplayDropdown(false);
      setDisplayCalendar(true);
      lastOpenContentRef.current = 'calendar';
      setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
      setView(CalendarViewEnum.Day);
      if (dateRangeState.startDate) {
        setCalendarNavigationDate(moment(dateRangeState.startDate));
      }
      setHoveredDate(null);
      return;
    }

    applyDropdownOption(option);
  };

  const isCustomDropdownOptionSelected = selectedDropdownOption === DateRangeDropdownOptionEnum.CustomDate;
  const shouldShowClearButton = withDateRangeDropdown ? isCustomDropdownOptionSelected : showClearButton;
  const isPopoverOpen = withDateRangeDropdown ? (displayDropdown || displayCalendar) : displayCalendar;
  const isRangeControlActive = withDateRangeDropdown ? isPopoverOpen : displayCalendar;
  const shouldRenderDropdownOptions = withDateRangeDropdown && lastOpenContentRef.current === 'dropdown';
  const isDesktopCustomDateCalendarOpen = withDateRangeDropdown
    && !shouldRenderDropdownOptions
    && browserWidthSize > ZOOM_BEHAVIOR_WIDTH;

  useEffect(() => {
    if (!isPopoverOpen) return undefined;

    acquireScrollLock();
    return () => releaseScrollLock();
  }, [isPopoverOpen]);
  const centeredAnchorPosition = isDesktopCustomDateCalendarOpen
    ? {
      top: Math.round(window.innerHeight / HALF_VIEWPORT_DIVISOR),
      left: Math.round(window.innerWidth / HALF_VIEWPORT_DIVISOR),
    }
    : undefined;

  const displaySelectedRange = () => {
    if (withDateRangeDropdown) {
      if (selectedDropdownOption !== DateRangeDropdownOptionEnum.CustomDate) {
        return <p className={styles.dropdownOptionText}>{getDropdownOptionLabel(selectedDropdownOption)}</p>;
      }

      const start = inputValueStartDate ? moment(inputValueStartDate) : null;
      const end = inputValueEndDate ? moment(inputValueEndDate) : null;

      if (!start || !end || !start.isValid() || !end.isValid()) {
        return <p className={styles.dropdownOptionText}>{getDropdownOptionLabel(selectedDropdownOption)}</p>;
      }

      if (start.isSame(end, 'day')) {
        return <p className={styles.dropdownOptionText}>{start.format(DAY_FIRST_CALENDAR_FORMAT)}</p>;
      }

      return <p className={styles.dropdownOptionText}>{`${start.format(DAY_FIRST_CALENDAR_FORMAT)} - ${end.format(DAY_FIRST_CALENDAR_FORMAT)}`}</p>;
    }

    const today = moment();
    const start = moment(inputValueStartDate);
    const end = moment(inputValueEndDate);

    if (!showClearButton && (singleDateSelection || !(start.isSame(today, 'day') && start.isSame(end, 'day')))) {
      return <p className={styles.dateText}>{lang.commonDate()}</p>;
    }

    if (start.isSame(today, 'day') && start.isSame(end, 'day')) {
      return <p className={styles.todayText}>{lang.commonDiscoverCalenderEventsToday()}</p>;
    }

    if (inputValueStartDate !== inputValueEndDate) {
      return (
        <>
          <span id="dateFrom" className={styles.dateRanges}>
            {start?.format(DAY_FIRST_CALENDAR_FORMAT)}
          </span>
          -
          <span id="dateTo" className={styles.dateRanges}>
            {end?.format(DAY_FIRST_CALENDAR_FORMAT)}
          </span>
        </>
      );
    }

    return (
      <span id="dateFrom" className={styles.dateRanges}>
        {start?.format(DAY_FIRST_CALENDAR_FORMAT)}
      </span>
    );
  };

  const renderDropdownOptions = () => {
    const options = [
      DateRangeDropdownOptionEnum.Tomorrow,
      DateRangeDropdownOptionEnum.OneWeek,
      DateRangeDropdownOptionEnum.OneMonth,
      DateRangeDropdownOptionEnum.CustomDate,
    ];

    return (
      <div className={styles.dropdownOptions}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={classNames(styles.dropdownOption, {
              [styles.dropdownOptionActive]: selectedDropdownOption === option,
            })}
            onClick={() => handleDropdownOptionSelect(option)}
          >
            {getDropdownOptionLabel(option)}
          </button>
        ))}
      </div>
    );
  };

  const handleViewChange = (event, inputView: 'month' | 'year') => {
    event.stopPropagation();

    setView((selectedView) => {
      if (selectedView === inputView) {
        return CalendarViewEnum.Day;
      }

      return inputView as CalendarViewEnum;
    });
  };

  const CustomCalendarHeader = (props: CalendarHeaderProps) => {
    const { currentMonth, minDate, maxDate, onMonthChange, view: propsView, disablePast, disableFuture } = props;
    const month = currentMonth.format('MMMM');
    const year = currentMonth.format('YYYY');
    const monthStart = currentMonth.clone().startOf('month');
    const nowMonth = moment().startOf('month');

    const minMonth = minDate ? moment(minDate).startOf('month') : undefined;
    const maxMonth = maxDate ? moment(maxDate).startOf('month') : undefined;

    const isPreviousDisabled = Boolean(
      (disablePast && monthStart.isSameOrBefore(nowMonth, 'month')) || (minMonth && monthStart.isSameOrBefore(minMonth, 'month')),
    );

    const isNextDisabled = Boolean(
      (disableFuture && monthStart.isSameOrAfter(nowMonth, 'month')) || (maxMonth && monthStart.isSameOrAfter(maxMonth, 'month')),
    );

    const handlePreviousMonth = (event) => {
      event.stopPropagation();
      if (isPreviousDisabled) {
        return;
      }

      onMonthChange(currentMonth.clone().subtract(1, 'month'), 'right');
    };

    const handleNextMonth = (event) => {
      event.stopPropagation();
      if (isNextDisabled) {
        return;
      }

      onMonthChange(currentMonth.clone().add(1, 'month'), 'left');
    };

    return (
      <div className={styles.customCalendarHeaderRoot}>
        <div className={styles.customCalendarHeader}>
          <Button
            className={styles.customCalendarHeaderButton}
            onClick={(e) => handleViewChange(e, CalendarViewEnum.Month)}
            variant={ButtonTypesEnum.Link}
          >
            <>
              {month}
              <ClickableImage
                src={DownArrow}
                className={classNames(styles.icon, styles.downArrow, {
                  [styles.rightIcon]: !shouldShowClearButton,
                  [styles.rightIconWithClearIcon]: shouldShowClearButton,
                  [styles.arrowTransform]: propsView === CalendarViewEnum.Month,
                })}
                alt="arrow icon"
              />
            </>
          </Button>
          <Button
            className={styles.customCalendarHeaderButton}
            onClick={(e) => handleViewChange(e, CalendarViewEnum.Year)}
            variant={ButtonTypesEnum.Link}
          >
            <>
              {year}
              <ClickableImage
                src={DownArrow}
                className={classNames(styles.icon, styles.downArrow, {
                  [styles.rightIcon]: !shouldShowClearButton,
                  [styles.rightIconWithClearIcon]: shouldShowClearButton,
                  [styles.arrowTransform]: propsView === CalendarViewEnum.Year,
                })}
                alt="arrow icon"
              />
            </>
          </Button>
        </div>
        {propsView === CalendarViewEnum.Day && (
          <div className={styles.customCalendarHeaderNavigation}>
            <IconButton
              className={styles.customCalendarNavButton}
              onClick={handlePreviousMonth}
              disabled={isPreviousDisabled}
            >
              <ArrowLeftIcon />
            </IconButton>
            <IconButton className={styles.customCalendarNavButton} onClick={handleNextMonth} disabled={isNextDisabled}>
              <ArrowRightIcon />
            </IconButton>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputWrapper}>
        <Button
          variant={ButtonTypesEnum.Secondary}
          className={classNames(styles.rangeWrapper, isRangeControlActive && styles.rangeWrapperActive)}
          onClick={openDateRangeCalendar}
        >
          <>
            <ClickableImage
              id="dateRangeIcon"
              src={DateRangeIcon}
              className={classNames(styles.icon, {
                [styles.rightIcon]: !shouldShowClearButton,
                [styles.rightIconWithClearIcon]: shouldShowClearButton,
              })}
              alt="cal icon"
            />
            {displaySelectedRange()}
            {!shouldShowClearButton && (
              <ClickableImage
                id="downArrow"
                src={DownArrow}
                className={classNames(styles.icon, {
                  [styles.rightIcon]: !shouldShowClearButton,
                  [styles.rightIconWithClearIcon]: shouldShowClearButton,
                  [styles.arrowTransform]: isPopoverOpen,
                })}
                alt="arrow icon"
              />
            )}
            {shouldShowClearButton && (
              <ClickableImage
                src={ClearIcon}
                className={styles.clearIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  clearDates();
                }}
              />
            )}
          </>
        </Button>
      </div>
      {errorMessage && <p className={styles.errorMsg}>{errorMessage}</p>}
      <Popover
        classes={{ paper: styles.paper }}
        open={isPopoverOpen}
        anchorEl={anchorEl}
        anchorReference={isDesktopCustomDateCalendarOpen ? 'anchorPosition' : 'anchorEl'}
        anchorPosition={centeredAnchorPosition}
        style={{ zoom: browserWidthSize <= ZOOM_BEHAVIOR_WIDTH ? `${Math.floor(browserWidthSize / 10).toString()}%` : '' }}
        className={styles.popover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: isDesktopCustomDateCalendarOpen ? 'center' : 'left',
        }}
        transformOrigin={{
          vertical: isDesktopCustomDateCalendarOpen ? 'center' : 'top',
          horizontal: isDesktopCustomDateCalendarOpen ? 'center' : 'left',
        }}
        slotProps={{
          paper: {
            style: {
              boxShadow: '0px 3px 6px  rgba(160,163,185,0.2)',
              marginTop:
                browserWidthSize <= ZOOM_BEHAVIOR_WIDTH
                  && !isDesktopCustomDateCalendarOpen
                  && location.pathname === UI_ROUTES.discover
                  ? `${paperPopover > 30 ? paperPopover + 20 : paperPopover}%`
                  : undefined,
              marginLeft:
                browserWidthSize <= ZOOM_BEHAVIOR_WIDTH && !isDesktopCustomDateCalendarOpen
                  ? `${paperPopover / 10}%`
                  : undefined,
            },
          },
          backdrop: {
            sx: {
              backgroundColor: shouldRenderDropdownOptions ? 'transparent !important' : null
            },
          },
        }}
        onClose={popoverClose}
        disableRestoreFocus
        disableScrollLock
      >
        {shouldRenderDropdownOptions ? (
          renderDropdownOptions()
        ) : (
          <div id="dateRangeWrapper" className={styles.dateRange}>
            {singleDateSelection ? (
              <div className={styles.selectedRange}>
                <span className={classNames(styles.selectedRangeDate, styles.activeSelectedRangeDate)}>
                  {moment(tempRange.startDate).format('MMM DD, YYYY')}
                </span>
              </div>
            ) : (
              <div className={styles.selectedRange}>
                <button
                  type="button"
                  className={classNames(styles.selectedRangeDate, {
                    [styles.activeSelectedRangeDate]: selectedRangeField === RangeSelectionFieldEnum.StartDate,
                    [styles.inactiveSelectedRangeDate]: selectedRangeField !== RangeSelectionFieldEnum.StartDate,
                  })}
                  onClick={() => {
                    setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
                    if (tempRange.startDate) {
                      setCalendarNavigationDate(moment(tempRange.startDate));
                    }
                    setHoveredDate(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedRangeField(RangeSelectionFieldEnum.StartDate);
                      if (tempRange.startDate) {
                        setCalendarNavigationDate(moment(tempRange.startDate));
                      }
                      setHoveredDate(null);
                    }
                  }}
                >
                  {moment(tempRange.startDate).format('MMM DD, YYYY')}
                </button>
                -
                <button
                  type="button"
                  className={classNames(styles.selectedRangeDate, {
                    [styles.activeSelectedRangeDate]: selectedRangeField === RangeSelectionFieldEnum.EndDate,
                    [styles.inactiveSelectedRangeDate]: selectedRangeField !== RangeSelectionFieldEnum.EndDate,
                  })}
                  onClick={() => {
                    setSelectedRangeField(RangeSelectionFieldEnum.EndDate);
                    if (tempRange.endDate) {
                      setCalendarNavigationDate(moment(tempRange.endDate));
                    }
                    setHoveredDate(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedRangeField(RangeSelectionFieldEnum.EndDate);
                      if (tempRange.endDate) {
                        setCalendarNavigationDate(moment(tempRange.endDate));
                      }
                      setHoveredDate(null);
                    }
                  }}
                >
                  {moment(tempRange.endDate).format('MMM DD, YYYY')}
                </button>
              </div>
            )}
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
              <DateCalendar
                value={calendarValue}
                onChange={handleDaySelect}
                minDate={minDateMoment}
                maxDate={effectiveMaxDate}
                views={['year', 'month', 'day']}
                className={styles.dateRangePicker}
                view={view}
                slots={{ day: RenderDay, calendarHeader: CustomCalendarHeader }}
                dayOfWeekFormatter={(day) => day.format('dd')}
                onViewChange={(nextView) => setView(nextView as CalendarViewEnum)}
                onYearChange={() => setView(CalendarViewEnum.Day)}
              />
            </LocalizationProvider>
            <div className={styles.dateRangeButtons}>
              <Button variant={ButtonTypesEnum.Secondary} className={styles.cancelBtn} onClick={handleCancel}>
                {lang.commonCancelButton()}
              </Button>
              <Button variant={ButtonTypesEnum.Primary} className={styles.applyBtn} onClick={handleApply}>
                {lang.commonApply()}
              </Button>
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};

export default DateRangePickerWrapper;
